import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useBusiness } from '../../../contexts/BusinessContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import { auth } from '../../../lib/firebase';
import { useToast } from '../../../hooks/use-toast';
import AIVoiceMetrics from '../Analytics/AIVoiceMetrics';
import { exportToCSV } from '../../../utils/exportUtils';
import { strategyExportRows } from '../../../lib/strategyExport';
import { buildRatePercentTrendModel, buildFormattedIntentBarModel } from '../../../lib/chartModels';
import { tenantHeaders } from '../../../lib/tenantSession';
import {
  TrendingUp, Users, Target, BarChart3, AlertTriangle, CheckCircle,
  Star, Clock, MessageSquare, Zap, Bot, Shield, RefreshCw,
  Download, Activity, Phone
} from 'lucide-react';

const StrategyDashboard = () => {
  const { currentBusiness } = useBusiness();
  const { toast } = useToast();
  const { t } = useLanguage();
  const [selectedPeriod, setSelectedPeriod] = useState('30d');
  const [activeTab, setActiveTab] = useState('resolution_trend');
  const [loading, setLoading] = useState(true);

  const [bizData, setBizData] = useState(null);
  const [chatData, setChatData] = useState(null);
  const [voiceData, setVoiceData] = useState(null);
  const [execData, setExecData] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_BASE || '';

  const fetchAll = useCallback(async () => {
    const businessId = currentBusiness?.brandId;
    if (!businessId) { setLoading(false); return; }
    setLoading(true);
    try {
      // Live Firebase ID token (auto-refreshed); cached token only as fallback (stale after ~1h → 401).
      const token = (auth.currentUser ? await auth.currentUser.getIdToken() : null) || localStorage.getItem('crm_access_token') || '';
      const h = { Authorization: `Bearer ${token}`, ...tenantHeaders() };
      // Removed the dead /analytics/ai-metrics fetch (result was stored in aiData and never rendered).
      const [b, c, v, e] = await Promise.allSettled([
        fetch(`${backendUrl}/api/v1/analytics/business/${businessId}?period=${selectedPeriod}`, { headers: h }),
        fetch(`${backendUrl}/api/v1/ai-chat/analytics/chat-metrics?business_id=${businessId}`, { headers: h }),
        fetch(`${backendUrl}/api/v1/ai-voice/business-stats/${businessId}`, { headers: h }),
        fetch(`${backendUrl}/api/v1/analytics/executive/${businessId}`, { headers: h }),
      ]);
      if (b.status === 'fulfilled' && b.value.ok) setBizData(await b.value.json());
      if (c.status === 'fulfilled' && c.value.ok) setChatData(await c.value.json());
      if (v.status === 'fulfilled' && v.value.ok) setVoiceData(await v.value.json());
      if (e.status === 'fulfilled' && e.value.ok) setExecData(await e.value.json());
    } catch (err) {
      toast({ title: t('common.error'), description: t('rd.errLoadStrategy'), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [currentBusiness?.brandId, selectedPeriod, backendUrl]);

  useEffect(() => {
    if (currentBusiness?.brandId) fetchAll();
    else setLoading(false);
  }, [currentBusiness?.brandId, selectedPeriod]);

  const fmt = (v, suffix = '') => v != null ? `${v}${suffix}` : '—';
  const fmtRate = (v) => v != null ? `${v}%` : '—';

  // Resolution trend + intent distribution (pure models in lib/chartModels, unit-tested).
  const resolutionTrend = buildRatePercentTrendModel(bizData?.daily_metrics);
  const intentData = buildFormattedIntentBarModel(chatData?.intent_distribution);

  if (loading) {
    return (
      <div className="flex-1 h-full flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="flex-1 h-full overflow-y-auto p-6 bg-gradient-to-br from-white via-green-50 to-purple-50">
      {/* Header */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('nav.strategyDashboard')}</h1>
          <p className="text-gray-600">{t('rd.operationalIntelligence')} — {currentBusiness?.name || t('rd.yourBusiness')}</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
            <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">{t('rd.days7')}</SelectItem>
              <SelectItem value="30d">{t('rd.days30')}</SelectItem>
              <SelectItem value="90d">{t('rd.days90')}</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchAll} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            {t('common.refresh')}
          </Button>
          <Button variant="outline" onClick={() => {
            // Export the ACTIVE tab's authoritative data (not a single generic bizData row).
            const rows = strategyExportRows(activeTab, { bizData, chatData, voiceData, execData });
            if (rows.length) exportToCSV(rows, `strategy-${activeTab}`);
          }}>
            <Download className="w-4 h-4 mr-2" />
            {t('rd.export')}
          </Button>
        </div>
      </div>

      {/* Top KPI row — real data only */}
      <div className="grid md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('rd.totalCustomers')}</p>
                <p className="text-3xl font-bold text-blue-600">{fmt(execData?.total_customers)}</p>
              </div>
              <Users className="h-8 w-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('rd.aiResolutionRate')}</p>
                <p className="text-3xl font-bold text-green-600">{fmtRate(bizData?.ai_resolution_rate)}</p>
              </div>
              <Bot className="h-8 w-8 text-green-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('rd.customerCsat')}</p>
                <p className="text-3xl font-bold text-purple-600">
                  {bizData?.customer_satisfaction != null ? `${bizData.customer_satisfaction}/5` : '—'}
                </p>
                <p className="text-xs text-gray-400">
                  {bizData?.csat_response_count ? t('rd.responsesCount', { count: bizData.csat_response_count }) : t('rd.noRatingsYet')}
                </p>
              </div>
              <Star className="h-8 w-8 text-purple-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('rd.ticketEscalationRate')}</p>
                <p className="text-3xl font-bold text-orange-600">{fmtRate(bizData?.escalation_rate)}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-orange-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="resolution_trend">{t('rd.resolutionTrend')}</TabsTrigger>
          <TabsTrigger value="intent_analysis">{t('rd.intentAnalysis')}</TabsTrigger>
          <TabsTrigger value="voice_channel">{t('rd.voiceChannel')}</TabsTrigger>
          <TabsTrigger value="operational">{t('rd.operationalKpis')}</TabsTrigger>
        </TabsList>

        {/* Resolution Trend */}
        <TabsContent value="resolution_trend" className="space-y-6">
          <Card>
            <CardHeader><CardTitle>{t('rd.resolutionEscalationRate')} — {selectedPeriod}</CardTitle></CardHeader>
            <CardContent>
              {resolutionTrend.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={resolutionTrend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis unit="%" domain={[0, 100]} />
                    <Tooltip formatter={(v) => `${v}%`} />
                    <Legend />
                    <Line type="monotone" dataKey="Resolution %" name={t('rd.resolutionPct')} stroke="#4CAF50" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="Escalation %" name={t('rd.escalationPct')} stroke="#F44336" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-48 flex items-center justify-center text-gray-400">{t('rd.noTicketData')}</div>
              )}
            </CardContent>
          </Card>

          <div className="grid md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-gray-500">{t('rd.totalTickets')}</p>
                <p className="text-2xl font-bold">{fmt(bizData?.total_tickets)}</p>
                <p className="text-xs text-gray-400">{t('rd.resolvedCount', { count: fmt(bizData?.resolved_tickets) })}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-gray-500">{t('rd.avgResolutionTime')}</p>
                <p className="text-2xl font-bold text-indigo-600">
                  {bizData?.avg_resolution_time != null ? `${bizData.avg_resolution_time}h` : '—'}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-gray-500">{t('rd.reopenedConversations')}</p>
                <p className={`text-2xl font-bold ${(bizData?.reopened_conversations || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {fmt(bizData?.reopened_conversations)}
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Intent Analysis */}
        <TabsContent value="intent_analysis" className="space-y-6">
          <Card>
            <CardHeader><CardTitle>{t('rd.customerIntentDistribution')}</CardTitle></CardHeader>
            <CardContent>
              {intentData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={intentData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-20} textAnchor="end" height={60} />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#9370DB" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-48 flex items-center justify-center text-gray-400">{t('rd.noChatDataPeriod')}</div>
              )}
            </CardContent>
          </Card>

          <div className="grid md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-gray-500">{t('rd.chatConversations')}</p>
                <p className="text-2xl font-bold">{fmt(chatData?.total_conversations)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-gray-500">{t('rd.autoResolved')}</p>
                <p className="text-2xl font-bold text-green-600">{fmt(chatData?.auto_resolved_count)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-gray-500">{t('rd.avgAiConfidence')}</p>
                <p className="text-2xl font-bold text-blue-600">
                  {chatData?.average_confidence != null ? `${Math.round(chatData.average_confidence * 100)}%` : '—'}
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Voice Channel */}
        <TabsContent value="voice_channel">
          <AIVoiceMetrics compact={false} />
        </TabsContent>

        {/* Operational KPIs */}
        <TabsContent value="operational" className="space-y-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { label: t('rd.callbackRate'), value: fmtRate(bizData?.callback_rate), note: t('rd.escalationsPendingCallback'), color: 'text-yellow-600' },
              { label: t('rd.openaiFallbackRate'), value: fmtRate(bizData?.openai_usage_rate), note: t('rd.gptWhenNoMatch'), color: 'text-orange-600' },
              { label: t('rd.openTickets'), value: fmt(execData?.active_tickets), note: t('rd.openRightNow'), color: 'text-blue-600' },
              { label: t('rd.voiceTotalCalls'), value: fmt(voiceData?.total_calls), note: t('rd.allInboundVoice'), color: 'text-purple-600' },
              { label: t('rd.voiceAiResolved'), value: fmt(voiceData?.ai_resolved), note: t('rd.withoutHumanTransfer'), color: 'text-green-600' },
              { label: t('rd.reopenedConversations'), value: fmt(bizData?.reopened_conversations), note: t('rd.requiredReengagement'), color: (bizData?.reopened_conversations || 0) > 0 ? 'text-red-600' : 'text-green-600' },
            ].map((item, i) => (
              <Card key={i} className="shadow-sm">
                <CardContent className="pt-6">
                  <p className="text-sm text-gray-500">{item.label}</p>
                  <p className={`text-2xl font-bold ${item.color}`}>{item.value}</p>
                  <p className="text-xs text-gray-400 mt-1">{item.note}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
            <Shield className="w-4 h-4 inline mr-2" />
            {t('rd.strategicMetricsNote')}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default StrategyDashboard;
